import { apiErrorText } from "../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `/live` — Live activity: every conversation in flight on every VoiceAgent (2026-09-29).
 *
 * The prototype's "Live activity" row opened the first agent's Live tab, and so did the console,
 * so a conversation on any other agent never showed there: during plan-007's live checks the
 * product owner called their second agent and watched this page stay empty. The page is now the
 * account's — oldest conversation first, each row naming its agent — and choosing a row opens its
 * transcript beside the list, exactly as an agent's Live tab does (the same `LiveTranscript`).
 *
 * What stays on the agent's own Live tab: the channel-unit meter and the day's figures, because
 * both belong to one agent. Each row's panel links there.
 *
 * The list is READ AGAIN EVERY FIVE SECONDS (`liveActivityQuery`) rather than streamed: the board
 * stream is per agent, and one stream per agent would open as many connections as the account has
 * agents. The transcript beside it is streamed, one conversation at a time.
 *
 * Not in the prototype: registered in `docs/design/exclusions.md`.
 *
 * AMENDED 2026-10-19 (plan-021 WAPP-HIST, design §33.13.3, OD-16): a conversation passed to another VoiceAgent of the
 * workspace names the one answering it now under its row — "Agent now: Delivery" — and "Agents in a conversation"
 * counts the VoiceAgents answering. A conversation that was never passed reads exactly as before.
 *
 * AMENDED 2026-10-28 (WAPP-ENDCALL, owner request): a row that is an OUTBOUND conversation (`outbound_origin` set — a
 * campaign call, an on-demand call) of the OPEN workspace carries "End call" for a person who may dial
 * (`../agents/[id]/end-outbound-conversation.tsx`). The end route reads the workspace from the sign-in, so another
 * workspace's row (this page lists every workspace the person belongs to) offers none; an inbound row never does.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Icon } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import {
  channelLabelWordOf,
  languageNameOf,
  partyLabelOf,
} from "../../api/conversation-view.js";
import { authContextQuery, languagePackQuery, liveActivityQuery } from "../../api/query.js";
import type { Medium } from "../../api/gateway.js";
import type { LiveActivityConversationView } from "../../api/slice/live-activity.js";
import { formatDuration } from "../../money.js";
import { GLYPH } from "../../shell/glyph.js";
import { betaLanguageLabelOf } from "../../shell/language-beta.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { EndOutboundConversation } from "../agents/[id]/end-outbound-conversation.js";
import { LiveTranscript } from "../agents/[id]/live-tab.js";

import "../agents/[id]/agent-detail.css";
import "./live-activity.css";

const MEDIUM_GLYPH = {
  voice: GLYPH.phoneCall,
  web_chat: GLYPH.browser,
  whatsapp: GLYPH.whatsappLogo,
  viber: GLYPH.chatCircleDots,
} as const satisfies Record<Medium, unknown>;

/** How many different agents are answering the conversations in flight (plan-021: the acting VoiceAgent). */
export function voiceAgentCountOf(conversation: readonly LiveActivityConversationView[]): number {
  return new Set(conversation.map((one) => one.actingStint?.voiceAgentId ?? one.voiceAgentId)).size;
}

/**
 * plan-021: "Agent now: Delivery" — the VoiceAgent answering a conversation another one entered, or `null` when the one
 * it reached is still answering.
 */
export function actingVoiceAgentTextOf(one: LiveActivityConversationView): string | null {
  const acting = one.actingStint;
  if (acting === undefined || acting.voiceAgentId === one.voiceAgentId) return null;
  return `${portalText("voiceAgentPassHistory", "live.column_acting")}: ${acting.displayName}`;
}

/** The row's second line: agent · how it came in · language · how far it has got. */
export function liveActivityMetaOf(
  one: LiveActivityConversationView,
  languageName: (code: string | null) => string,
): string {
  return [one.voiceAgentName, channelLabelWordOf(one.channelLabel), languageName(one.languageCode), one.phase].join(
    " · ",
  );
}

export function LiveActivityScreen(): ReactNode {
  usePortalTranslation("operations");
  usePortalTranslation("voiceAgentPassHistory");
  const gateway = useGateway();
  const activity = useQuery(liveActivityQuery(gateway));
  const languagePack = useQuery(languagePackQuery(gateway));
  const [chosenConversationId, setChosenConversationId] = useState("");
  // WAPP-ENDCALL: "End call" for a person who may dial, on the open workspace's outbound rows.
  const mayDial = useMemberCan().can("conversation.dial");
  const authSession = useQuery(authContextQuery(gateway)).data;
  const openWorkspaceId = authSession?.kind === "workspace_selected" ? authSession.workspaceId : null;
  const [endedConversationId, setEndedConversationId] = useState<ReadonlySet<string>>(new Set());
  const markEnded = (conversationId: string): void => {
    setEndedConversationId((current) => new Set([...current, conversationId]));
  };

  // plan-026 (0176): the row's line is text, so a beta language reads "Spanish (Beta)".
  const languageName = (code: string | null): string =>
    code === null
      ? "—"
      : betaLanguageLabelOf(languagePack.data, code, languagePack.data?.find((one) => one.code === code)?.name ?? languageNameOf(code));

  const head = (
    <div className="la-head">
      <h1 className="c-va-title">{portalText("operations", "live_activity_97935e2e")}</h1>
    </div>
  );

  if (activity.data === undefined) {
    return (
      <section className="c-va la-page" data-chat="none">
        {head}
        {activity.isError ? (
          <p className="c-note c-note-critical">
            {portalText("operations", "what_is_happening_right_now_could_not_be_read_5ed828cd")}{apiErrorText(activity.error, "")}{portalText("operations", "reload_to_try_again_80fb0954")}</p>
        ) : (
          <p className="c-note">{portalText("operations", "reading_what_is_happening_right_now_60347263")}</p>
        )}
      </section>
    );
  }

  const conversation = activity.data.conversation.filter((one) => !endedConversationId.has(one.conversationId));
  // A chosen conversation that has ended leaves the list; the panel moves to the oldest one left.
  const selected = conversation.find((one) => one.conversationId === chosenConversationId) ?? conversation[0];

  return (
    <section className="c-va la-page" data-chat="none">
      {head}

      <div className="c-va-figures">
        <div>
          <span className="c-va-figure-value">{String(activity.data.liveConversationCount)}</span>
          <span className="c-va-figure-label">{portalText("operations", "live_conversations_cca62c52")}</span>
        </div>
        <div>
          <span className="c-va-figure-value">{String(voiceAgentCountOf(conversation))}</span>
          <span className="c-va-figure-label">{portalText("operations", "agents_in_a_conversation_4ff59ae0")}</span>
        </div>
      </div>

      {activity.isError ? (
        <p className="c-note c-note-critical">
          {portalText("operations", "the_list_could_not_be_read_again_0ef2e37d")}{apiErrorText(activity.error, "")}{portalText("operations", "what_is_shown_may_be_out_of_date_86565d1b")}</p>
      ) : null}

      <div className="c-va-split">
        <div>
          <p className="c-va-label">{portalText("operations", "live_conversations_cca62c52")}</p>
          {conversation.length === 0 ? (
            <p className="c-note">
              {portalText("operations", "nothing_is_on_a_call_right_now_this_list_fills_the_moment_3f691583")}</p>
          ) : (
            <div className="c-va-live-list">
              {conversation.map((one) => {
                const row = (
                <button
                  key={one.conversationId}
                  type="button"
                  className="c-va-live-row"
                  aria-current={one.conversationId === selected?.conversationId}
                  onClick={() => {
                    setChosenConversationId(one.conversationId);
                  }}
                >
                  <span className="c-va-live-row-head">
                    <span className="c-va-live-row-party">
                      <span className="c-va-live-row-icon">
                        <Icon glyph={MEDIUM_GLYPH[one.medium]} size="lg" />
                      </span>
                      <span className="c-va-live-row-name">{partyLabelOf(one)}</span>
                      {one.isTest ? <span className="tag tag-neutral la-tag">{portalText("operations", "test_640ab2ba")}</span> : null}
                    </span>
                    <span className="c-va-live-row-duration">{formatDuration(one.elapsedSeconds)}</span>
                  </span>
                  <span className="c-va-live-row-meta">{liveActivityMetaOf(one, languageName)}</span>
                  {actingVoiceAgentTextOf(one) === null ? null : (
                    <span className="c-va-live-row-meta">{actingVoiceAgentTextOf(one)}</span>
                  )}
                </button>
                );
                const outboundOrigin = one.outboundOrigin ?? null;
                return outboundOrigin === null || !mayDial || one.workspaceId !== openWorkspaceId ? (
                  row
                ) : (
                  <div key={one.conversationId} className="c-va-live-item">
                    {row}
                    <EndOutboundConversation
                      conversationId={one.conversationId}
                      outboundOrigin={outboundOrigin}
                      partyText={partyLabelOf(one)}
                      onEnded={markEnded}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selected === undefined ? null : (
          <div className="la-panel">
            <LiveTranscript
              key={selected.conversationId}
              conversationId={selected.conversationId}
              partyAddress={selected.partyAddress}
            />
            <p className="la-panel-foot">
              <span>
                {selected.voiceAgentName} · {selected.workspaceName}
              </span>
              <Link
                to="/agents/$voiceAgentId"
                params={{ voiceAgentId: selected.voiceAgentId }}
                search={{ tab: "live", range: "7d", outcome: "all", q: "", conversation: selected.conversationId }}
                className="btn btn-ghost"
              >
                {portalText("operations", "open_the_agent_s_live_tab_081a0576")}</Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
